import { format, Locale, setDay } from 'date-fns';
import { resolveDateFnsLocale, resolveWeekStartsOn } from '@hqo/date-time';

// One object per (date-fns locale, week start): callers pass the result straight into props and hook
// dependencies, so a fresh object per call would re-render them on every pass.
const resolvedLocales = new Map<string, Locale>();

/**
 * Resolve a date-fns Locale from an i18n locale string.
 *
 * Words come from the shared resolver's date-fns object; the week start comes from CLDR for the
 * locale's REGION, because a language-level date-fns object cannot carry a regional fact (date-fns
 * `pt` starts on Monday, Portugal starts on Sunday). Region-less tags borrow the device's region.
 */
export function getDateFnsLocale(locale?: string): Locale {
  // useLocale() falls back through config.locale -> building locale -> navigator.language, and the
  // store values are not guaranteed to be strings; anything else resolves as "no locale".
  const tag = typeof locale === 'string' && locale ? locale : undefined;
  const base = resolveDateFnsLocale(tag) as Locale;
  const weekStartsOn = resolveWeekStartsOn(tag) ?? base.options?.weekStartsOn;

  if (weekStartsOn === base.options?.weekStartsOn) {
    return base;
  }

  const key = `${base.code}|${weekStartsOn}`;
  const cached = resolvedLocales.get(key);
  if (cached) {
    return cached;
  }

  const resolved: Locale = { ...base, options: { ...base.options, weekStartsOn } };
  resolvedLocales.set(key, resolved);

  return resolved;
}

/**
 * Get the index of the first day of the week for a locale.
 * 0 = Sunday, 1 = Monday
 */
export const getWeekStartsOn = (locale: Locale): number => locale?.options?.weekStartsOn ?? 0;

/**
 * Day keys in standard order (Sunday first)
 */
export const STANDARD_DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export type DayKey = typeof STANDARD_DAY_KEYS[number];

/**
 * Get weekday keys ordered according to the locale's week start.
 * Example:
 * - enUS → ['sunday', 'monday', ...]
 * - de → ['monday', 'tuesday', ...]
 */
export function getLocalizedWeekdayKeys(locale: Locale): readonly DayKey[] {
  const weekStartsOn = getWeekStartsOn(locale);
  return [...STANDARD_DAY_KEYS.slice(weekStartsOn), ...STANDARD_DAY_KEYS.slice(0, weekStartsOn)];
}

/**
 * Get a localized weekday name for a given day key.
 * @param dayKey - Day key (e.g., 'monday')
 * @param locale - date-fns Locale object
 * @param formatStr - Format string (default: 'EEEE' for full name)
 * @returns Localized day name
 */
export function getLocalizedDayName(dayKey: DayKey, locale: Locale, formatStr: string = 'EEEE'): string {
  const dayIndex = STANDARD_DAY_KEYS.indexOf(dayKey);
  // Use a reference date (January 5, 2025, is a Sunday)
  const referenceDate = setDay(new Date(2025, 0, 5), dayIndex, { weekStartsOn: 0 });
  return format(referenceDate, formatStr, { locale });
}
